import * as React from 'react';
import {
    useLogger,
    useServiceConfigEndpointProvider,
    useSpaAsyncConfig
} from '../../context';
import {useLoadData} from './useLoadData';
import FeatureAppLoadingContainer, {
    ContainerSize
} from '../feature-app/FeatureAppLoadingContainer';
import {
    VictorTenantType,
    IncentiveQueryParams,
    Incentive,
    IncentiveBffResponse
} from '@volkswagen-onehub-cms/cms-victor-bff';
import fetch from '@strictsoftware/fetch';

export interface IncentiveLoaderProps {
    path: string;
    incentiveId?: string;
    victorTenantType: VictorTenantType;
    contentUpdater: (path: string, incentive: Incentive) => void;
    children: React.ReactNode;
}

export const IncentiveLoader = (props: IncentiveLoaderProps) => {
    const {children, incentiveId, victorTenantType, path, contentUpdater} =
        props;
    const cmsVictorBffBaseUrl = useSpaAsyncConfig().cmsVictorBffBaseUrl;
    const logger = useLogger().incentive;

    const oneapiKey = useSpaAsyncConfig().oneApiConfiguration.apiKey;
    const {endpoint, cacheBustingAllowed} = useServiceConfigEndpointProvider();

    const globalDataId = 'incentive:' + path + '/' + incentiveId;

    const loadFn = React.useCallback(() => {
        if (incentiveId && cmsVictorBffBaseUrl) {
            const params: IncentiveQueryParams = {
                incentiveId: incentiveId,
                victorTenantType,
                endpoint,
                oneapiKey,
                endpointCacheBustingEnabled: cacheBustingAllowed
            };
            const url = new URL(`${cmsVictorBffBaseUrl}/bff/incentive`);
            Object.entries(params).forEach(([key, value]) =>
                url.searchParams.append(key, value)
            );
            return fetch(url.toString()).then(data => data.json());
        }
        return Promise.resolve(undefined);
    }, [
        incentiveId,
        victorTenantType,
        endpoint,
        oneapiKey,
        cacheBustingAllowed,
        cmsVictorBffBaseUrl
    ]);

    const updaterFn = React.useCallback(
        (data?: IncentiveBffResponse) => {
            if (data) {
                const newIncentive = data.incentive;

                if (newIncentive) {
                    // update current model with victor data
                    contentUpdater(path, newIncentive);
                } else {
                    logger.warn('no incentive found');
                }
            }
        },
        [contentUpdater, logger, path]
    );

    const data = useLoadData<IncentiveBffResponse | undefined>({
        globalDataId,
        load: loadFn,
        onLoad: updaterFn
    });

    if (!incentiveId || !cmsVictorBffBaseUrl) {
        return children;
    }
    if (data.state === 'loading') {
        return (
            <FeatureAppLoadingContainer
                size={ContainerSize.SMALL}
                visualFeedback
            />
        );
    }
    if (data.state === 'rejected') {
        return null;
    }
    return children;
};
